import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faNewspaper } from "@fortawesome/free-solid-svg-icons";
import { useColumn, useAuth } from "../../util/hook";

const stripHtml = (html: string) => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const ColumnSection = () => {
  const navigate = useNavigate();
  const { columns, loading, error } = useColumn();
  const { isLoggedIn } = useAuth();

  // 화면 크기에 따른 excerpt 길이
  const [excerptLength, setExcerptLength] = useState(40);

  useEffect(() => {
    const updateExcerptLength = () => {
      if (window.innerWidth <= 768) {
        setExcerptLength(20); // 모바일
      } else if (window.innerWidth <= 1024) {
        setExcerptLength(30); // 태블릿
      } else {
        setExcerptLength(40); // 데스크탑
      }
    };

    updateExcerptLength();
    window.addEventListener("resize", updateExcerptLength);
    return () => window.removeEventListener("resize", updateExcerptLength);
  }, []);

  const displayedColumns = columns.slice(0, 5);

  const handleColumnClick = (columnId: number) => {
    navigate(`/column/${columnId}`, { state: { backTo: "/" } });
  };

  return (
    <Section className="column-section" id="column">
      <Container className="container">
        <SectionHeader className="section-header">
          <SectionTitle className="section-title">
            <FontAwesomeIcon icon={faNewspaper} className="section-icon" /> 칼럼
          </SectionTitle>
          <SectionTitleEng className="section-title-eng">
            COLUMN
          </SectionTitleEng>
          <SectionDesc className="section-desc">
            교회의 생각을 나눕니다
          </SectionDesc>
        </SectionHeader>

        {isLoggedIn && (
          <WriteButtonWrapper>
            <WriteButton
              onClick={() => navigate("/column/new", { replace: true })}
            >
              글쓰기
            </WriteButton>
          </WriteButtonWrapper>
        )}

        {loading && <LoadingMessage>칼럼을 불러오는 중...</LoadingMessage>}

        {error && (
          <ErrorMessage>칼럼을 불러오는 중 오류가 발생했습니다.</ErrorMessage>
        )}

        {!loading && !error && columns.length === 0 && (
          <EmptyMessage>
            등록된 칼럼이 없습니다.
            {isLoggedIn && (
              <EmptyActionButton
                onClick={() => navigate("/column/new", { replace: true })}
              >
                첫 칼럼 등록하기
              </EmptyActionButton>
            )}
          </EmptyMessage>
        )}

        {!loading && !error && columns.length > 0 && (
          <>
            <ColumnTable className="column-table">
              <thead>
                <tr>
                  <th className="col-title">제목</th>
                  <th className="col-excerpt">내용 미리보기</th>
                  <th className="col-date">날짜</th>
                </tr>
              </thead>
              <tbody>
                {displayedColumns.map((column) => {
                  const excerpt = stripHtml(column.content).substring(
                    0,
                    excerptLength,
                  );
                  return (
                    <ColumnRow
                      key={column.id}
                      className="column-item"
                      onClick={() => handleColumnClick(column.id)}
                    >
                      <td className="col-title">{column.title}</td>
                      <td className="col-excerpt">
                        {excerpt}
                        {excerpt.length >= excerptLength ? "..." : ""}
                      </td>
                      <td className="col-date">{column.date}</td>
                    </ColumnRow>
                  );
                })}
              </tbody>
            </ColumnTable>

            <ViewAllButton onClick={() => navigate("/columns")}>
              전체보기 ({columns.length}개)
            </ViewAllButton>
          </>
        )}
      </Container>
    </Section>
  );
};

const Section = styled.section`
  padding: 80px 0;
  background: #fff;
  position: relative;

  &:nth-child(even) {
    background: #f9f9f9;
  }

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  @media (max-width: 480px) {
    padding: 40px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }

  @media (max-width: 480px) {
    padding: 0 10px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  color: #004094;
  margin-bottom: 5px;
  letter-spacing: -1px;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 700;

  .section-icon {
    font-size: 0.9em;
    color: #004094;
  }

  @media (max-width: 768px) {
    font-size: 24px;
    flex-direction: column;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    gap: 5px;
  }
`;

const SectionTitleEng = styled.span`
  font-size: 14px;
  color: #999;
  display: block;
  margin-bottom: 20px;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-weight: 700;
`;

const SectionDesc = styled.p`
  font-size: 18px;
  color: #555;
  font-weight: 300;
  word-break: keep-all;
`;

const WriteButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 30px;
`;

const WriteButton = styled.button`
  padding: 10px 20px;
  background: #004094;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;

  &:hover {
    background: #003070;
  }
`;

const ColumnTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: center;
  font-size: 15px;

  thead th {
    padding: 15px;
    background: #f4f4f4;
    color: #333;
    border-top: 2px solid #333;
    border-bottom: 1px solid #ccc;
    font-weight: 700;
  }

  tbody td {
    padding: 12px 15px;
    border-bottom: 1px solid #eee;
    color: #555;
    font-weight: 300;
  }

  tbody tr:hover {
    background: #f9fbfd;
  }

  .col-title {
    width: 30%;
    text-align: left;
    padding-left: 30px !important;
    color: #222;
    cursor: pointer;
    font-weight: 700;
  }

  .col-excerpt {
    width: 50%;
    text-align: center;
    color: #666;
    font-size: 14px;
  }

  .col-date {
    width: 20%;
    color: #888;
    font-size: 14px;
  }

  @media (max-width: 1024px) {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
    font-size: 14px;
  }

  @media (max-width: 768px) {
    font-size: 12px;

    thead th {
      padding: 10px 8px;
    }

    tbody td {
      padding: 10px 8px;
    }

    .col-title {
      padding-left: 15px !important;
    }
  }
`;

const ColumnRow = styled.tr`
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    background: #f9f9f9;
  }

  &:hover .col-title {
    color: #004094;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #c33;
  background-color: #fee;
  border-radius: 4px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const EmptyActionButton = styled.button`
  padding: 12px 24px;
  background: #004094;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;

  &:hover {
    background: #003070;
  }
`;

const ViewAllButton = styled.button`
  display: block;
  margin: 30px auto 0;
  padding: 12px 30px;
  background: #004094;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  font-size: 15px;

  &:hover {
    background: #003070;
  }
`;

export default ColumnSection;
